import React, { useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { createFormData } from '../../services/api';
import api from '../../services/api';

const PhotoUpload = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPG, PNG, GIF, or BMP file.');
      return;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setError('File is too large. Maximum size is 10MB.');
      return;
    }

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    setSelectedFile(file);
    setError(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please upload a JPG, PNG, GIF, or BMP file.');
        return;
      }

      // Check file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setError('File is too large. Maximum size is 10MB.');
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = createFormData({
        photo: selectedFile,
        private: isPrivate
      });

      // Upload the photo with progress tracking
      const response = await api.post('/photos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      setUploadSuccess(true);

      // Reset form
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsPrivate(false);

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/photos');
      }, 2000);
    } catch (error) {
      console.error('Error uploading photo:', error);

      if (error.response && error.response.data && error.response.data.errors) {
        const errorMessages = Object.values(error.response.data.errors)
          .flat()
          .join(' ');
        setError(errorMessages);
      } else {
        setError('Failed to upload photo. Please try again later.');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0">Upload Photo</h2>
        <Button as={Link} to="/photos" variant="outline-primary">
          <i className="bi bi-arrow-left me-2"></i>
          Back to Photos
        </Button>
      </div>

      {uploadSuccess && (
        <Alert variant="success" className="mb-4">
          Photo uploaded successfully! Redirecting to your photo gallery...
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={7} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-3">Select Photo</h5>

              <div
                className="upload-area border rounded p-4 text-center mb-3"
                style={{
                  minHeight: '300px',
                  background: '#f8f9fa',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onClick={() => fileInputRef.current.click()}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="img-fluid mb-3"
                    style={{ maxHeight: '300px' }}
                  />
                ) : (
                  <>
                    <i className="bi bi-cloud-arrow-up display-1 text-muted mb-3"></i>
                    <h5>Drag & Drop or Click to Upload</h5>
                    <p className="text-muted mb-0">
                      Supported formats: JPG, PNG, GIF, BMP (Max size: 10MB)
                    </p>
                  </>
                )}

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.gif,.bmp"
                  style={{ display: 'none' }}
                />
              </div>

              {selectedFile && (
                <div className="mb-3">
                  <p className="mb-1">
                    <strong>Selected File:</strong> {selectedFile.name}
                  </p>
                  <p className="mb-0">
                    <strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="private-photo"
                  label="Make this photo private (only visible to users you've matched with)"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                />
                {isPrivate && (
                  <Alert variant="info" className="mt-2 mb-0 py-1 px-2">
                    <small>Note: Private photos cannot be set as profile pictures</small>
                  </Alert>
                )}
              </Form.Group>

              {uploading && (
                <div className="mb-3">
                  <p className="mb-1">Uploading...</p>
                  <ProgressBar
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    animated
                  />
                </div>
              )}

              <div className="d-flex justify-content-between">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/photos')}
                  disabled={uploading}
                >
                  Cancel
                </Button>

                <Button
                  variant="primary"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                >
                  {uploading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Uploading...
                    </>
                  ) : (
                    'Upload Photo'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-3">Photo Guidelines</h5>

              <div className="mb-3">
                <h6 className="fw-bold">Do:</h6>
                <ul className="mb-0">
                  <li>Upload clear, high-quality photos</li>
                  <li>Use recent photos that look like you</li>
                  <li>Include at least one clear face photo</li>
                  <li>Show your interests and personality</li>
                  <li>Smile and look approachable</li>
                </ul>
              </div>

              <div className="mb-3">
                <h6 className="fw-bold">Don't:</h6>
                <ul className="mb-0">
                  <li>Use heavily filtered or edited photos</li>
                  <li>Upload photos with other people</li>
                  <li>Include inappropriate or explicit content</li>
                  <li>Use copyrighted images or stock photos</li>
                  <li>Upload photos of children</li>
                </ul>
              </div>

              <Alert variant="info" className="mb-0">
                <i className="bi bi-info-circle-fill me-2"></i>
                All photos are reviewed by our team before being approved. This process usually takes 24-48 hours.
              </Alert>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-3">Photo Privacy</h5>

              <p className="mb-3">
                <strong>Public Photos:</strong> Visible to all users on the platform. Only public photos can be set as your profile picture.
              </p>

              <p className="mb-0">
                <strong>Private Photos:</strong> Only visible to users you've matched with. Private photos cannot be set as profile pictures.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PhotoUpload;
